import PropTypes from 'prop-types';
import { CheckBox, CheckBoxOutlineBlank } from 'styled-icons/material';
import { StickyNote } from 'styled-icons/fa-regular';
import {
  CheckIcon,
  DocumentTextIcon,
  DownloadIcon,
} from '@heroicons/react/outline';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toIntlDateTimeFormat } from '@zedoc/date';
import { saveAs } from 'file-saver';
import { EAPP_STATE__RESOLVED } from '../../../../../common/schemata/EAPP';
import {
  EAPP_ACTION_TYPE__SEND_NOTIFICATION,
  EAPP_ACTION_TYPE__SEND_NOTIFICATION_TO_ASSIGNEE,
} from '../../../../../common/schemata/ProjectEAPPRule';
import {
  REPORT_TYPE__EAPPS_ALERT_SUMMARY_SINGLE_DOWNLOAD,
  REPORT_TYPE__EAPPS_ALERT_SUMMARY_PATIENT_DOWNLOAD,
} from '../../../../../common/constants/collections/Projects';
import Badge from '../../../../../common/components/Badge';
import Table from '../../../../../common/components/Table';
import Tag from '../../../../../common/components/Tag';
import Tooltip from '../../../../../common/components/Tooltip';
import Select from '../../../../../components/inputs/Select';
import Card from '../../../../../components/Card';
import Button from '../../../../../components/Button';
import Modal from '../../../../../components/dialogs/Modal';
import List from '../../../../../common/components/List';
import branding from '../../../../../utils/branding';
import { callMethod } from '../../../../../common/utilsClient/ddp/actions';
import { apiZedocGetCustomPDF } from '../../../../../common/api/zedoc';
import { openEAPPDialog } from '../../../actions';
import DashboardNotificationModal from '../DashboardNotificationModal';
import DashboardResolveModal from '../DashboardResolveModal';
import PatientColumn from './PatientColumn';
import { selectAllProjectMilestones, selectProject } from '../../../selectors';
import CurrentUser from '../../../../../models/CurrentUser';
import KeywordsDialog from './components/KeywordsDialog';
import { getPriorityType } from './helpers';
// import ScheduleVideoCall from './ScheduleVideoCall';

// NOTE: That should be a flag coming from "settings.public" or ENV var
const USE_KEYWORDS = true;

const DownloadButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  min-width: 200px;
`;

const DashboardTable = ({
  dataSource,
  pagination,
  loading,
  assignees: assigneesProp,
  onChangeAssignee,
  onAssigneeDropdownOpen,
  hideColumns,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const dispatch = useDispatch();

  const [isNotificationModalVisible, setIsNotificationModalVisible] =
    useState(false);
  const [isResolveModalVisible, setIsResolveModalVisible] = useState(false);
  const [currentRecordId, setCurrentRecordId] = useState(null);
  const [isReportSelectionModalVisible, setIsReportSelectionModalVisible] =
    useState(false);
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [currentRecord, setCurrentRecord] = useState(null);

  const handleCloseNotificationModal = () => {
    setCurrentRecordId(null);
    setIsNotificationModalVisible(false);
  };
  const handleOpenNotesModal = (id) => {
    dispatch(
      openEAPPDialog({
        eappId: id,
      }),
    );
  };
  const handleOpenResolveModal = (id) => {
    setCurrentRecordId(id);
    setIsResolveModalVisible(true);
  };
  const handleCloseResolveModal = () => {
    setCurrentRecordId(null);
    setIsResolveModalVisible(false);
  };

  const projectMilestones = useSelector(selectAllProjectMilestones);
  const project = useSelector(selectProject);
  const currentUser = useSelector(CurrentUser.select.user());
  const currentUserId = currentUser?._id;

  // Get published reports by type
  // Try to get pdfReports from project (could be direct property or in _raw)
  const pdfReports = useMemo(() => {
    if (!project) {
      return [];
    }

    const reports = project.pdfReports || project._raw?.pdfReports || [];
    return reports;
  }, [project]);

  const publishedSingleReports = useMemo(() => {
    if (!pdfReports || pdfReports.length === 0) return [];
    return pdfReports.filter(
      (report) =>
        report.reportType ===
          REPORT_TYPE__EAPPS_ALERT_SUMMARY_SINGLE_DOWNLOAD &&
        report.published === true,
    );
  }, [pdfReports]);

  const publishedPatientReports = useMemo(() => {
    if (!pdfReports || pdfReports.length === 0) return [];
    return pdfReports.filter(
      (report) =>
        report.reportType ===
          REPORT_TYPE__EAPPS_ALERT_SUMMARY_PATIENT_DOWNLOAD &&
        report.published === true,
    );
  }, [pdfReports]);

  // Helper function to check if a report should be shown for a given milestoneId
  const shouldShowReportForMilestone = (report, milestoneId) => {
    if (!milestoneId) return false;

    // If useAllMilestones is true, show for all milestones
    if (report.useAllMilestones === true) {
      return true;
    }

    // If useAllMilestones is false or undefined, check if milestoneId is in milestones array
    if (report.milestones && Array.isArray(report.milestones)) {
      return report.milestones.includes(milestoneId);
    }

    // If no milestones array, don't show (shouldn't happen, but safety check)
    return false;
  };

  // Helper function to get filtered reports for a specific milestoneId
  const getFilteredReportsForMilestone = (reports, milestoneId) => {
    if (!milestoneId) return [];
    return reports.filter((report) =>
      shouldShowReportForMilestone(report, milestoneId),
    );
  };

  // User-specific download history helpers (only check for current logged-in user)
  const hasUserDownloadedSingleAlertPDF = (record) => {
    if (!currentUserId || !record?.downloadHistory?.singleAlertPDF)
      return false;
    const byUser = record.downloadHistory.singleAlertPDF[currentUserId];
    return (
      byUser && typeof byUser === 'object' && Object.keys(byUser).length > 0
    );
  };

  const hasUserDownloadedAllForPatient = (record) => {
    if (!currentUserId || !record?.downloadHistory?.allForPatient) return false;
    const byUser = record.downloadHistory.allForPatient[currentUserId];
    return (
      byUser && typeof byUser === 'object' && Object.keys(byUser).length > 0
    );
  };

  const hasUserDownloadedTemplate = (record, reportType, templateId) => {
    if (!currentUserId || !record?.downloadHistory?.[reportType]) return false;
    const byUser = record.downloadHistory[reportType][currentUserId];
    return byUser && byUser[templateId];
  };

  const shouldShowDownloadColumn = useMemo(() => {
    return (
      publishedSingleReports.length > 0 || publishedPatientReports.length > 0
    );
  }, [publishedSingleReports, publishedPatientReports]);

  const handleDownloadAlertPDF = (record) => {
    const filteredReports = getFilteredReportsForMilestone(
      publishedSingleReports,
      record.milestoneId,
    );

    if (filteredReports.length === 0) return;

    if (filteredReports.length === 1) {
      // Direct download if only one report
      downloadReport(filteredReports[0], {
        eappId: record._id,
        projectId: project?._id,
        userId: currentUserId,
      });
    } else {
      // Show modal for multiple reports
      setSelectedReportType(REPORT_TYPE__EAPPS_ALERT_SUMMARY_SINGLE_DOWNLOAD);
      setCurrentRecord(record);
      setIsReportSelectionModalVisible(true);
    }
  };

  const handleDownloadAllForPatient = (record) => {
    const filteredReports = getFilteredReportsForMilestone(
      publishedPatientReports,
      record.milestoneId,
    );

    if (filteredReports.length === 0) return;

    if (filteredReports.length === 1) {
      // Direct download if only one report
      downloadReport(filteredReports[0], {
        patientId: record.recipientId,
        projectId: project?._id,
        eappId: record._id, // Track which EAPP row triggered the download
        userId: currentUserId,
      });
    } else {
      // Show modal for multiple reports
      setSelectedReportType(REPORT_TYPE__EAPPS_ALERT_SUMMARY_PATIENT_DOWNLOAD);
      setCurrentRecord(record);
      setIsReportSelectionModalVisible(true);
    }
  };

  const downloadReport = (reportConfig, references) => {
    dispatch(
      callMethod(apiZedocGetCustomPDF, {
        references,
        audience: 'provider',
        language,
        templateId: reportConfig.id,
        reportType: reportConfig.reportType,
      }),
    )
      .then((url) => {
        const blob = new Blob([url], {
          type: 'application/pdf',
        });
        const filename = `${reportConfig.name || 'report'}-${Date.now()}.pdf`;
        saveAs(blob, filename);
      })
      .catch((error) => {
        console.error('Error downloading PDF:', error);
      });
  };

  const handleReportSelect = (reportConfig) => {
    if (!currentRecord) return;

    const references =
      selectedReportType === REPORT_TYPE__EAPPS_ALERT_SUMMARY_SINGLE_DOWNLOAD
        ? {
            eappId: currentRecord._id,
            projectId: project?._id,
            userId: currentUserId,
          }
        : {
            patientId: currentRecord.recipientId,
            projectId: project?._id,
            eappId: currentRecord._id, // Track which EAPP row triggered the download
            userId: currentUserId,
          };

    downloadReport(reportConfig, references);
    setIsReportSelectionModalVisible(false);
    setCurrentRecord(null);
    setSelectedReportType(null);
  };

  const handleCloseReportSelectionModal = () => {
    setIsReportSelectionModalVisible(false);
    setCurrentRecord(null);
    setSelectedReportType(null);
  };

  const availableReports = useMemo(() => {
    if (!currentRecord) return [];

    const milestoneId = currentRecord.milestoneId;

    if (
      selectedReportType === REPORT_TYPE__EAPPS_ALERT_SUMMARY_SINGLE_DOWNLOAD
    ) {
      return getFilteredReportsForMilestone(
        publishedSingleReports,
        milestoneId,
      );
    }
    if (
      selectedReportType === REPORT_TYPE__EAPPS_ALERT_SUMMARY_PATIENT_DOWNLOAD
    ) {
      return getFilteredReportsForMilestone(
        publishedPatientReports,
        milestoneId,
      );
    }
    return [];
  }, [
    selectedReportType,
    publishedSingleReports,
    publishedPatientReports,
    currentRecord,
  ]);

  // const renderActionType = ({
  //   id,
  //   actionType,
  // }) => {
  //   const handleOpenNotificationModal = () => {
  //     setCurrentRecordId(id);
  //     setIsNotificationModalVisible(true);
  //   };

  //   switch (actionType) {
  //     case 'Notification':
  //       return (
  //         <ButtonLink onClick={handleOpenNotificationModal}>
  //           {actionType}
  //         </ButtonLink>
  //       );
  //     default:
  //       return (
  //         <Text.Span
  //           style={{
  //             whiteSpace: 'nowrap',
  //           }}
  //         >
  //           {actionType}
  //         </Text.Span>
  //       );
  //   }
  // };

  const getActionTypeTranslation = (type) => {
    let translationKey = `eapp:${type}`;

    if (type === EAPP_ACTION_TYPE__SEND_NOTIFICATION) {
      translationKey = 'notificationSent';
    }

    if (type === EAPP_ACTION_TYPE__SEND_NOTIFICATION_TO_ASSIGNEE) {
      return 'Notification to assignee';
    }

    return t(translationKey);
  };

  const projectVariables = project?.variables;
  const hhsNameVariable = projectVariables.find((x) => x.name === 'hhs_name');
  const hhsCodeVariable = projectVariables.find((x) => x.name === 'hhs_code');
  const facilityNameVariable = projectVariables.find(
    (x) => x.name === 'facility_name',
  );
  const urnVariable = projectVariables.find((x) =>
    ['urn', 'patient_urn'].includes(x.name),
  );

  const columns = useMemo(() => {
    const baseColumns = [
      ...(shouldShowDownloadColumn
        ? [
            {
              title: t('download'),
              dataIndex: 'download',
              key: 'download',
              render: (_, record) => {
                const filteredSingleReports = getFilteredReportsForMilestone(
                  publishedSingleReports,
                  record.milestoneId,
                );
                const filteredPatientReports = getFilteredReportsForMilestone(
                  publishedPatientReports,
                  record.milestoneId,
                );

                return (
                  <DownloadButtonsContainer>
                    {filteredSingleReports.length > 0 && (
                      <Button
                        type="ghost"
                        icon={<DocumentTextIcon />}
                        onClick={() => handleDownloadAlertPDF(record)}
                      >
                        <span>{t('alertPDF')}</span>
                        {hasUserDownloadedSingleAlertPDF(record) && (
                          <CheckIcon
                            className="text-success"
                            style={{ width: '16px', height: '16px' }}
                          />
                        )}
                      </Button>
                    )}
                    {filteredPatientReports.length > 0 && (
                      <Button
                        type="ghost"
                        icon={<DownloadIcon />}
                        onClick={() => handleDownloadAllForPatient(record)}
                      >
                        <span>{t('allForPatient')}</span>
                        {hasUserDownloadedAllForPatient(record) && (
                          <CheckIcon
                            className="text-success"
                            style={{ width: '16px', height: '16px' }}
                          />
                        )}
                      </Button>
                    )}
                  </DownloadButtonsContainer>
                );
              },
            },
          ]
        : []),
      {
        title: t('date'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (createdAt) =>
          toIntlDateTimeFormat(language, createdAt, {
            dateStyle: 'short',
            timeStyle: 'short',
          }),
      },
      {
        title: t('referenceNo'),
        dataIndex: 'referenceNo',
        key: 'referenceNo',
      },
      {
        title: t('state'),
        dataIndex: 'state',
        key: 'state',
        render: (state, record) => (
          <Button
            type="ghost"
            icon={
              state === EAPP_STATE__RESOLVED ? (
                <CheckBox size="24" />
              ) : (
                <CheckBoxOutlineBlank size="24" />
              )
            }
            isIconSizeFixed={false}
            disabled={state === EAPP_STATE__RESOLVED}
            onClick={
              state !== EAPP_STATE__RESOLVED
                ? () => handleOpenResolveModal(record._id)
                : () => {}
            }
          />
        ),
      },
      {
        title: t('notes'),
        dataIndex: 'notes',
        key: 'notes',
        fixed: 'left',
        width: '55px',
        render: (notes, record) => (
          <Badge
            type="info"
            // NOTE: Eslint misinterprets this as a component function,
            //       i.e. first argument being props.
            // eslint-disable-next-line react/destructuring-assignment
            count={notes && notes.length}
            offsetX={-8}
            offsetY={8}
          >
            <Button
              type="ghost"
              icon={<StickyNote />}
              onClick={() => handleOpenNotesModal(record._id)}
            />
          </Badge>
        ),
      },
      {
        title: t('recipients', {
          count: 1,
          context: branding,
        }),
        dataIndex: 'recipientId',
        key: 'recipientId',
        width: 192,
        render: (
          recipientId,
          { projectId, recipient, patientName, patientBirthYear },
        ) => (
          <PatientColumn
            recipient={recipient}
            projectId={projectId}
            patientName={patientName}
            patientBirthYear={patientBirthYear}
          />
        ),
      },
      hhsNameVariable
        ? {
            title: 'HHS Name',
            dataIndex: hhsNameVariable?.id,
            key: `patientVariables.${hhsNameVariable?.id}`,
            width: 192,
            render: (patientVariables, row) => (
              <span>{row.patientVariables[hhsNameVariable?.id]}</span>
            ),
          }
        : null,
      hhsCodeVariable
        ? {
            title: 'HHS Code',
            dataIndex: hhsCodeVariable?.id,
            key: `patientVariables.${hhsCodeVariable?.id}`,
            width: 192,
            render: (patientVariables, row) => (
              <span>{row.patientVariables[hhsCodeVariable?.id]}</span>
            ),
          }
        : null,
      facilityNameVariable
        ? {
            title: 'Facility Name',
            dataIndex: facilityNameVariable?.id,
            key: `patientVariables.${facilityNameVariable?.id}`,
            width: 192,
            render: (patientVariables, row) => (
              <span>{row.patientVariables[facilityNameVariable?.id]}</span>
            ),
          }
        : null,
      urnVariable
        ? {
            title: 'URN',
            dataIndex: urnVariable?.id,
            key: `patientVariables.${urnVariable?.id}`,
            width: 192,
            render: (patientVariables, row) => (
              <span>{row.patientVariables[urnVariable?.id]}</span>
            ),
          }
        : null,
      // {
      //   title: 'prp_notification',
      //   dataIndex: 'prp_notification',
      //   key: 'prp_notification',
      //   render: bool => (bool ? 'Yes' : 'No'),
      // },
      {
        title: 'Assignees',
        dataIndex: 'assignees',
        key: 'assignees',
        width: '350px',
        render: (assignees, record) =>
          record.state === EAPP_STATE__RESOLVED ? (
            assigneesProp
              .filter(({ value }) =>
                record.assignees?.find((x) => x.userId === value),
              )
              .map((o) => o.label)
              .join(', ')
          ) : (
            <Select
              size="default"
              options={assigneesProp}
              value={assignees?.map(({ userId }) => userId) || []}
              placeholder="No assignee"
              multiple
              showSearch={true}
              onFocus={() => {
                if (onAssigneeDropdownOpen) {
                  onAssigneeDropdownOpen(record.recipientId);
                }
              }}
              onChange={(selected) => {
                onChangeAssignee({
                  assignees: assigneesProp.filter(({ value }) =>
                    selected.includes(value),
                  ),
                  eappId: record._id,
                });
              }}
            />
          ),
      },
      {
        title: t('priority'),
        dataIndex: 'priority',
        key: 'priority',
        render: (priority) => (
          <Tag type={getPriorityType(priority)}>{t(priority)}</Tag>
        ),
      },
      {
        title: t('name'),
        dataIndex: 'name',
        key: 'name',
        render: (name, record) => (
          <Tooltip title={record.description}>{name}</Tooltip>
        ),
      },
      {
        title: t('milestones', { count: 1 }),
        dataIndex: 'milestoneId',
        key: 'milestoneId',
        render: (milestoneId) => {
          const record = projectMilestones.find(
            ({ _id }) => _id === milestoneId,
          );
          return <Tooltip title={record?.type}>{record?.name}</Tooltip>;
        },
      },
      {
        title: t('actionType'),
        dataIndex: 'actionsToTrigger',
        key: 'actionsToTrigger',
        width: '350px',
        // render: (_, record) => renderActionType(record),
        render: (actionsToTrigger) =>
          [
            ...new Set([
              ...actionsToTrigger.map(({ type }) =>
                getActionTypeTranslation(type),
              ),
            ]),
          ].join(', '),
      },
      // {
      //   title: t('videoCall'),
      //   dataIndex: 'videoCall',
      //   key: 'videoCall',
      //   render: (_, {
      //     recipient,
      //     assigneeId,
      //     recipientId,
      //   }) => (
      //     <ScheduleVideoCall
      //       identifier={recipient.getNationalId()}
      //       birthYear={recipient.getYearOfBirth()}
      //       name={recipient.getFullName()}
      //       assigneeId={assigneeId}
      //       recipientId={recipientId}
      //     />
      //   ),
      // },
    ];

    if (USE_KEYWORDS) {
      baseColumns.push(
        {
          title: t('triggerKeywords'),
          dataIndex: 'keywords',
          key: 'keywords',
          render: (_, record) => {
            const [isOpen, setIsOpen] = useState(false);

            const { displayKeywords, highlightKeywords, excludeKeywords } =
              useMemo(() => {
                const {
                  keywordMatchRulesKeywords,
                  keywordNegatedRulesKeywords,
                } = record;

                if (keywordMatchRulesKeywords) {
                  const positive = keywordMatchRulesKeywords
                    .split(',')
                    .map((keyword) => keyword.trim())
                    .filter(Boolean);
                  const negated = (keywordNegatedRulesKeywords || '')
                    .split(',')
                    .map((keyword) => keyword.trim())
                    .filter(Boolean);

                  // Normalize for comparison (case-insensitive, strip parentheses)
                  const normalize = (word) =>
                    word
                      .replace(/^\(|\)$/g, '')
                      .trim()
                      .toLowerCase();

                  const positiveSet = new Set(positive.map(normalize));
                  const negatedSet = new Set(negated.map(normalize));

                  const overlapping = new Set(
                    [...positiveSet].filter((w) => negatedSet.has(w)),
                  );

                  const filteredPositive = positive.filter(
                    (word) => !overlapping.has(normalize(word)),
                  );
                  const filteredNegated = negated.filter(
                    (word) => !overlapping.has(normalize(word)),
                  );

                  return {
                    displayKeywords: [...new Set(filteredPositive)],
                    highlightKeywords: [...new Set(filteredPositive)],
                    excludeKeywords: [...new Set(filteredNegated)],
                  };
                }

                return {
                  displayKeywords: [],
                  highlightKeywords: [],
                  excludeKeywords: [],
                };
              }, [record]);

            return (
              <>
                <button
                  type="button"
                  className="text-primary underline hover:no-underline text-left"
                  onClick={() => setIsOpen(true)}
                >
                  {displayKeywords.join(', ')}
                </button>
                <KeywordsDialog
                  isOpen={isOpen}
                  onCancel={() => setIsOpen(false)}
                  referenceNo={record.referenceNo}
                  recipientFullName={record.recipient.getFullName()}
                  answersSheetId={record.answersSheetId}
                  keywordMatchRulesQuestionId={
                    record.keywordMatchRulesQuestionId
                  }
                  priority={record.priority}
                  keywords={highlightKeywords}
                  excludeKeywords={excludeKeywords}
                  createdAt={record.createdAt}
                />
              </>
            );
          },
        },
        {
          title: t('forms:questionId.label'),
          dataIndex: 'keywordMatchRulesQuestionId',
          key: 'keywordMatchRulesQuestionId',
          render: (_, record) => {
            return record.keywordMatchRulesQuestionId;
          },
        },
      );
    }

    return baseColumns.filter((x) => x);
  }, [
    shouldShowDownloadColumn,
    publishedSingleReports,
    publishedPatientReports,
    t,
    language,
    projectMilestones,
    hhsNameVariable,
    hhsCodeVariable,
    facilityNameVariable,
    urnVariable,
    assigneesProp,
    onChangeAssignee,
    onAssigneeDropdownOpen,
    handleOpenResolveModal,
    handleOpenNotesModal,
    getActionTypeTranslation,
    USE_KEYWORDS,
  ]);

  const filteredColumns = columns.filter((el) => {
    if (Array.isArray(hideColumns) && hideColumns.length > 0) {
      return !hideColumns.some(
        (item) => item.toLowerCase().trim() === el.title.toLowerCase().trim(),
      );
    }
    return true;
  });

  const notifications = [
    {
      id: '1',
      status: 'Delivered',
      timestamp: new Date(),
      destination: 'email@email.com',
    },
    {
      id: '2',
      status: 'Failed',
      timestamp: new Date(),
      destination: '+64123456789',
    },
  ];

  return (
    <>
      <DashboardResolveModal
        eappId={dataSource.find(({ _id }) => _id === currentRecordId)?._id}
        patientName={dataSource
          .find(({ _id }) => _id === currentRecordId)
          ?.recipient?.getFullName()}
        visible={isResolveModalVisible}
        onCancel={handleCloseResolveModal}
      />
      <DashboardNotificationModal
        title={dataSource.find(({ id }) => id === currentRecordId)?.recipientId}
        dataSource={notifications}
        visible={isNotificationModalVisible}
        onCancel={handleCloseNotificationModal}
      />
      <Modal
        title={t('Select Report')}
        visible={isReportSelectionModalVisible}
        onCancel={handleCloseReportSelectionModal}
        footer={[
          <Button key="cancel" onClick={handleCloseReportSelectionModal}>
            {t('Cancel')}
          </Button>,
        ]}
      >
        <List
          dataSource={availableReports}
          renderItem={(report) => {
            const reportType =
              selectedReportType ===
              REPORT_TYPE__EAPPS_ALERT_SUMMARY_SINGLE_DOWNLOAD
                ? 'singleAlertPDF'
                : 'allForPatient';
            const hasDownloaded = currentRecord
              ? hasUserDownloadedTemplate(currentRecord, reportType, report.id)
              : false;
            return (
              <div
                key={report.id}
                style={{
                  padding: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onClick={() => handleReportSelect(report)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleReportSelect(report);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <div style={{ color: '#1890ff' }}>{report.name}</div>
                {hasDownloaded && (
                  <CheckIcon
                    className="text-success"
                    style={{ flexShrink: 0, width: '16px', height: '16px' }}
                  />
                )}
              </div>
            );
          }}
        />
      </Modal>
      <Card>
        <Table
          size="small"
          dataSource={dataSource}
          columns={filteredColumns}
          loading={loading}
          pagination={pagination}
          scroll={{
            x: true,
          }}
        />
      </Card>
    </>
  );
};

DashboardTable.propTypes = {
  loading: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  pagination: PropTypes.objectOf(PropTypes.any),
  assignees: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  onChangeAssignee: PropTypes.func.isRequired,
  onAssigneeDropdownOpen: PropTypes.func,
  hideColumns: PropTypes.arrayOf(PropTypes.string),
  // eslint-disable-next-line react/forbid-prop-types
  dataSource: PropTypes.arrayOf(PropTypes.any),
};

DashboardTable.defaultProps = {
  loading: false,
  pagination: null,
  assignees: [],
  onAssigneeDropdownOpen: null,
  hideColumns: null,
  dataSource: [],
};

export default DashboardTable;
